// SYNTHETIC demo data for the MHI-V1 VSD screw vacuum pump, a fictional design.
// Not from any real manual or product; every value is invented and round
// (cad/VACUUM_SPEC.md §3.3-§3.4, §6). Fault ids are V<system>-<nn> (e.g. V32-01)
// and `ata` carries the system code, as the part knowledge pack does.

export const vacuumPumpStatus = {
  designation: 'MHI-V1',
  serial: 'DEMO-V01',
  status: 'Running · at maximum speed (set point not reached)',
  location: 'Process hall B, line 2',
  runningHours: 19880,
  nextService: { kit: 'SK-4000', dueInHours: 120, overdue: ['SK-8000', 'SK-16000'] },
  serviceHistory: [
    { hours: 8000, fitted: 'SK-8000' },
    { hours: 12000, fitted: 'SK-4000' },
    { hours: 16000, fitted: 'SK-4000', note: 'SK-16000 and SK-8000 due; only the 4,000 h kit fitted' },
    { hours: 19712, fitted: '-', note: 'Over-temperature call-out: cooler cleaned; thermostatic insert left locked in the full-cooler position and cooler fan left forced on' },
  ],
  // pumpingSpeedM3h: the controller's figure from the last pump-down over 100 -> 50 mbar(a) (630 when healthy).
  inlet: { pressureMbarA: 32, setPointMbarA: 10, pumpingSpeedM3h: 210, pumpDownMin: 9, pumpDownBaselineMin: 4 },
  gasBallast: { commanded: 'Open', coilCurrentMa: 0, state: 'Commanded open · coil current 0 mA (open circuit)' },
  nonReturnValve: { riseAfterStopMbar: 983, lastStopHours: 19861 },
  drive: {
    speedRpm: 4000, shaftPowerKw: 11.0, motorCurrentA: 21, windingTempC: 88, outputHz: 67,
    typicalAtSetPoint: '2,700 rpm · 6.5 kW',
  },
  element: { outletTempC: 72, vibrationMmS: 2.4, blankOffMbarA: 0.8, blankOffHours: 16000 },
  oil: {
    // 100 %RS: saturated, free water present; waterPpm is the lab sample (sensor tops out at saturation).
    temperatureC: 60, levelPct: 58, saturationPct: 100, waterPpm: 4000, filterDpMbar: 120,
    filterHours: 3880, hoursSinceChange: 11880,
  },
  // dpMbar is logged at the last pump-down (exhaust then 1,100 mbar(a)); exhaustMbarA is now, at low gas flow.
  separator: { dpMbar: 480, elementHours: 11880, exhaustMbarA: 1020, oilMist: 'Visible (operator report)' },
  cooling: { airTempC: 31, fan: 'On (service override)', fanCurrentA: 2.1, coolerPort: 'Full open (insert locked)' },
  electrical: { heatsinkC: 58, dcBusV: 565, gateway: 'Online' },
  frame: { doors: 'Closed' },
  specificEnergyWhM3: 52.4, // last pump-down: 11,000 W shaft power / 210 m3/h (not the current operating point)
};

/**
 * The five hero faults plus the service notice. Same shape as aircraftFaults,
 * plus `aliases`: lower-case phrases that name the fault in plain speech
 * (matchFault in data/faultSets.js, and the knowledge pack's fault aliases).
 */
export const vacuumPumpFaults = [
  {
    id: 'V30-01',
    ata: 'V30',
    severity: 'warning',
    status: 'active',
    category: 'vacuum performance',
    title: 'Target vacuum not reached, slow pump-down',
    description: 'Inlet at 32 mbar(a) against a 10 mbar(a) set point after 20 minutes at maximum speed. Pump-down of '
      + 'the 15 m³ system to 50 mbar(a) took 9 minutes against a 4 minute baseline; the calculated pumping '
      + 'speed over 100 to 50 mbar(a) fell from 630 to 210 m³/h. With the 1,200 mbar·L/s process load, the '
      + 'net inlet speed at 32 mbar(a) is 37 L/s. The last blank-off, 0.8 mbar(a) at 16,000 h, passed. The '
      + 'evidence points at the pump, not the process: the oil is saturated with water (V40-01) that '
      + 're-evaporates in the element, and a sticky non-return valve flap throttles the inlet. A healthy pump '
      + 'holds the set point with about 120 L/s at 2,700 rpm and 6.5 kW shaft power; at 4,000 rpm shaft power '
      + 'is 11 kW, so the fault costs about 4.5 kW, roughly 110 kWh a day. Run the rate-of-rise test to confirm '
      + 'the process side is tight before changing parts.',
    recommendation: 'The evidence points at the pump: confirm the process side is tight with the rate-of-rise test, then dry the oil and free the non-return valve flap.',
    affectedComponent: 'Inlet and oil',
    aliases: [
      'target vacuum', 'vacuum not reached', 'not reaching vacuum', 'reach vacuum', 'reaching vacuum', 'pull vacuum',
      'poor vacuum', 'bad vacuum', 'pump down', 'pumpdown', 'slow pump down', 'pull down', 'pulldown',
      'pulling down', 'slow to pull', 'pump or the process', 'pump or my process', 'pump or the system',
      'pump or system', 'is it the pump', 'leak test', 'costing', 'energy cost',
      'takes forever', 'takes ages', 'evacuate', 'evacuation', 'base pressure', 'ultimate pressure', 'vacuum level',
    ],
  },
  {
    id: 'V40-01',
    ata: 'V40',
    severity: 'warning',
    status: 'active',
    category: 'oil',
    title: 'Water in oil, oil emulsified',
    description: 'Oil in the sight glass is milky: the moisture sensor reads 100 % saturation, so free water is '
      + 'present, and a lab sample at 19,860 h found 4,000 ppm (saturation at the 70 °C sump is about 3,000 '
      + 'ppm; high warning at 95 %RS). Injected oil is at 60 °C and the element outlet at 72 °C, below the 80 '
      + '°C outlet minimum for condensable duty. The gas ballast is commanded open but its coil draws 0 mA '
      + '(open circuit), so the ballast valve stays closed. At the over-temperature call-out at 19,712 h the '
      + 'thermostatic insert was locked in the full-cooler position and the cooler fan forced on in service '
      + 'override; neither was undone, so all the oil is cooled all the time. With the ballast open the pump '
      + 'has a water vapour tolerance of 40 mbar and a water vapour capacity of 20 kg/h; closed, the vapour '
      + 'condenses in the oil.',
    recommendation: 'Replace the ballast solenoid coil, free the thermostatic insert, clear the cooler fan override, dry the oil with the ballast open, then change the oil.',
    affectedComponent: 'Oil circuit',
    aliases: [
      'water in oil', 'water in the oil', 'milky oil', 'oil is milky', 'emulsified', 'emulsion', 'moisture',
      'condensation', 'wet oil', 'cold oil', 'gas ballast', 'milky', 'looks milky', 'cloudy oil',
      'creamy', 'cloudy', 'mayonnaise', 'oil is wet', 'oil so wet', 'water in my oil',
    ],
  },
  {
    id: 'V51-01',
    ata: 'V51',
    severity: 'warning',
    status: 'active',
    category: 'exhaust',
    title: 'Separator differential pressure high, oil mist at exhaust',
    description: 'Separator dP 480 mbar at the last pump-down from atmosphere, at full gas load (warning '
      + 'above 400 mbar, replace at 600 mbar). The exhaust pressure at that moment was 1,100 mbar(a), so the '
      + 'vessel stood at about 1,580 mbar(a), below the 2,000 mbar(a) relief valve setting. Oil mist is '
      + 'reported at the exhaust outlet and the oil level fell from 64 % to 58 % in 200 running hours. The '
      + 'element has run 11,880 h on an 8,000 h change interval, and the oil is emulsified (V40-01).',
    recommendation: 'Check the scavenge flow and oil condition, then replace the separator element.',
    affectedComponent: 'Oil separator',
    aliases: [
      'oil mist', 'separator pressure', 'separator differential', 'separator differential pressure', 'separator dp',
      'oil carry over', 'oil carryover', 'smoking exhaust', 'oil at the exhaust', 'mist', 'misting', 'smoke',
      'smoky', 'exhaust filter', 'oil mist filter', 'oil consumption', 'consuming oil', 'using oil', 'losing oil',
    ],
  },
  {
    id: 'V10-01',
    ata: 'V10',
    severity: 'critical',
    status: 'resolved',
    category: 'cooling',
    title: 'Element outlet over-temperature shutdown (cleared, 19,712 h)',
    description: 'At 19,712 h the pump tripped at 112 °C element outlet temperature (warning 105 °C, shutdown 110 °C). '
      + 'Oil was at 102 °C and cooling air at 41 °C; the cooler fan was commanded on and drew 2.0 A (normal '
      + '2.1 A), so it was running. The cooler core was cleaned on the air side and the pump restarted with '
      + 'the thermostatic insert locked in the full-cooler position and the fan forced on in service '
      + 'override. Neither was undone, which is why the oil now runs cold (V40-01). Before the next hot '
      + 'season, check for hot-air recirculation and a blocked rear grille, prove the insert and fan, and '
      + 'clear the fan override.',
    recommendation: 'Clean the cooler, check the rear grille for recirculation, prove the fan motor and thermostatic insert, clear the fan override, and check oil level and filter.',
    affectedComponent: 'Oil cooler and fan',
    aliases: [
      'over temperature', 'overtemperature', 'overheat', 'overheating', 'high temperature', 'temperature shutdown',
      'trip', 'tripped',
    ],
  },
  {
    id: 'V32-01',
    ata: 'V32',
    severity: 'critical',
    status: 'active',
    category: 'inlet',
    title: 'Oil back-streaming into the process after stop',
    description: 'At the last stop (19,861 h), made with the inlet isolation valve closed, the spool pressure rose '
      + 'from 30 to 1,013 mbar(a) in 8 s (limit: 50 mbar rise in the first 10 s). The spool holds about 5 L, '
      + 'so gas flowed back at about 600 mbar·L/s: the non-return valve is not sealing, and oil was found in '
      + 'the spool. At stop the pump side rises to vessel pressure through the stationary element, and gas '
      + 'and oil flow back to whatever is still at vacuum; an earlier stop made with the isolation valve open '
      + 'left oil in the process line. The 16,000 h kit, which renews the flap, spring and seat, was not '
      + 'fitted. Until the valve is rebuilt, close the inlet isolation valve before every stop, not only for '
      + 'maintenance, and check the process line for oil.',
    recommendation: 'Rebuild the non-return valve; prove the oil stop valve closes at stop.',
    affectedComponent: 'Non-return valve',
    aliases: [
      'back streaming', 'backstreaming', 'suck back', 'suckback', 'oil in the process', 'oil in the inlet',
      'oil back', 'flowing back', 'sucked back', 'sucking back', 'sucks back', 'into my chamber', 'into the chamber',
      'oil in the vacuum line', 'oil in the process line', 'oil in the reactor', 'oil in my process',
      'check valve leaking', 'leaking back',
    ],
  },
  {
    id: 'V74-01',
    ata: 'V74',
    severity: 'info',
    status: 'active',
    category: 'service',
    title: '4,000 h service due',
    description: 'The 4,000 h service kit (SK-4000) is due in 120 running hours, at 20,000 h. The 8,000 h and '
      + '16,000 h kits due at 16,000 h were not fitted and are overdue (see V74-02).',
    recommendation: 'Fit the 16,000 h kit at this visit (it includes the 8,000 h and 4,000 h parts) and bring a ballast solenoid coil.',
    affectedComponent: 'Service kit',
    aliases: [
      'service due', 'next service', 'service kit', 'due at the next service',
    ],
  },
];

/**
 * Isolation chain every vacuum-pump task starts with; the industrial
 * counterpart of the EV high-voltage protocol (VACUUM_SPEC.md §7.2, in order).
 */
export const vacuumSafetySteps = [
  'If the process gas is hazardous, first close the inlet isolation valve and purge with inert gas through the purge port while the pump still runs',
  'Close the inlet isolation valve, stop the pump, then lock out and tag out the main isolator',
  'Tag the inlet isolation valve closed so the pump stays isolated from the process',
  'Vent the pump to atmospheric pressure through the purge port, and the inlet spool through the vacuum gauge port (a closed non-return valve keeps the spool at vacuum): with inert gas, not air, if the process gas is hazardous, and then cycle-purge the spool three times (evacuate it with a service pump, refill with inert gas) before it is lifted',
  'Let hot oil and surfaces cool until safe to touch (oil above 60 °C burns); drain oil warm, wearing gloves',
  'The separator vessel may hold residual pressure: confirm the local vessel gauge on the relief-valve tee reads atmospheric (1 bar(a)), then crack the filler plug slowly behind a rag before opening anything',
];

/** Short chip labels for a task's `service_kit` (knowledge pack service kits). */
export const SERVICE_KIT_LABELS = {
  'SK-4000': '4,000 h kit',
  'SK-8000': '8,000 h kit',
  'SK-16000': '16,000 h kit',
  'SK-EOH': 'Element overhaul',
};

export default vacuumPumpStatus;
